"use client";

import { useTranslation } from "react-i18next";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { CalendarClock } from "lucide-react";
import { useBookingCta, useBookingImpression } from "@/features/booking/use-booking-cta";
import { topbarPillBaseClasses } from "@/components/layout/balance-button";

/**
 * Persistent topbar "Book a call" CTA for sales-qualified users.
 *
 * Visibility = `booking-cta-topbar` feature flag AND the server-computed
 * `salesQualified` profile field (business-domain users, or free-email users
 * with a professional job title). Unlike the powerspot pill's book_meeting
 * variant this is not part of the priority rotation — it renders at all times
 * for the qualified audience once the flag ramps.
 *
 * Calendar routing: paid-active accounts get the account-team calendar
 * (customer-meeting); everyone else gets the consult calendar — logged-in
 * users need an implementation/architecture conversation, not a product demo.
 */
export function BookCallButton() {
  const { t } = useTranslation();
  const enabled = useFeatureValue("booking-cta-topbar", false);
  const cta = useBookingCta("topbar_book_call");
  useBookingImpression(cta, "topbar_book_call", enabled && cta.qualified);

  if (!enabled || !cta.qualified) return null;

  const { label, href } = cta;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={cta.trackClick}
      className={`${topbarPillBaseClasses} border-primary/30 text-primary hover:bg-primary/10`}
      aria-label={t("TOPBAR.BOOK_CALL_ARIA", "Book a call with the Speak AI team")}
      data-testid="topbar-book-call"
    >
      <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">{t(label.key, label.text)}</span>
    </a>
  );
}
